import React, { useMemo, useState } from "react";
import Input from "components/Input";
import TextArea from "components/TextArea";
import { FIELD_MASK_OPTIONS } from "../../../../constants";
import {
  Container,
  Header,
  Title,
  Grid,
  InputGroup,
  Label,
  ToggleRow,
  Section,
  SectionToggle,
  SectionContent,
  SectionHint,
  NativeSelect,
  HelperText,
  ActionRow,
  ActionButton,
  OptionRow,
  AddOptionButton,
} from "./styles";

function normalizeId(label) {
  return (label || "campo")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "campo";
}

function getMaskTypeFromPattern(pattern) {
  const presetOption = FIELD_MASK_OPTIONS.find(
    (option) => option.value !== "none" && option.value !== "custom" && option.pattern === pattern
  );

  if (presetOption) {
    return presetOption.value;
  }

  return pattern ? "custom" : "none";
}

function FormFieldEditor({ field, onChange, onClose }) {
  const [showValidation, setShowValidation] = useState(
    Boolean(
      field.required ||
        field.minLength ||
        field.maxLength ||
        field.pattern ||
        field.errorMessage
    )
  );

  const updateField = (updates) => {
    const nextField = { ...field, ...updates };

    if (updates.label !== undefined) {
      nextField.id = normalizeId(updates.label);
    }

    onChange(nextField);
  };

  const updateOption = (index, key, value) => {
    const nextOptions = [...(field.options || [])];
    nextOptions[index] = {
      ...nextOptions[index],
      [key]: value,
    };
    updateField({ options: nextOptions });
  };

  const addOption = () => {
    updateField({
      options: [...(field.options || []), { label: "", value: "" }],
    });
  };

  const removeOption = (index) => {
    updateField({
      options: (field.options || []).filter((_, currentIndex) => currentIndex !== index),
    });
  };

  const hasOptions = ["checkbox", "radio", "select"].includes(field.type);
  const isSlider = field.type === "slider";
  const isImageClick = field.type === "image_click";
  const isSummary = field.type === "summary";
  const isTextualField = ["text", "textarea"].includes(field.type);
  const supportsDefaultValue = !["thermal_capture", "image_click", "summary"].includes(field.type);
  const selectedMaskType = useMemo(
    () => field.maskType || getMaskTypeFromPattern(field.pattern),
    [field.maskType, field.pattern]
  );

  const handleMaskChange = (event) => {
    const nextMaskType = event.target.value;

    if (nextMaskType === "none") {
      updateField({ maskType: nextMaskType, pattern: "" });
      return;
    }

    if (nextMaskType === "custom") {
      updateField({ maskType: nextMaskType });
      return;
    }

    const selectedMask = FIELD_MASK_OPTIONS.find((option) => option.value === nextMaskType);
    updateField({
      maskType: nextMaskType,
      pattern: selectedMask?.pattern || "",
      placeholder: selectedMask?.placeholder || field.placeholder,
    });
  };

  return (
    <Container>
      <Header>
        <Title>Configurar campo</Title>
      </Header>

      <Grid>
        <InputGroup>
          <Label>Tipo</Label>
          <Input value={field.type} disabled />
        </InputGroup>
        <InputGroup>
          <Label>ID</Label>
          <Input value={field.id} disabled />
        </InputGroup>
        <InputGroup>
          <Label>Label</Label>
          <Input
            value={field.label}
            onChange={(e) => updateField({ label: e.target.value })}
            placeholder="Label do campo"
          />
        </InputGroup>
        {!isSummary ? (
          <InputGroup>
            <Label>Placeholder</Label>
            <Input
              value={field.placeholder}
              onChange={(e) => updateField({ placeholder: e.target.value })}
              placeholder="Placeholder opcional"
            />
          </InputGroup>
        ) : null}
      </Grid>

      {supportsDefaultValue ? (
        <InputGroup style={{ marginTop: 16 }}>
          <Label>Valor padrão</Label>
          <Input
            value={field.defaultValue}
            type={isSlider ? "number" : "text"}
            onChange={(e) => updateField({ defaultValue: e.target.value })}
            placeholder={
              field.type === "checkbox"
                ? "valor-1, valor-2"
                : "Valor preenchido automaticamente"
            }
          />
          {field.type === "checkbox" ? (
            <HelperText>Informe os valores separados por vírgula.</HelperText>
          ) : null}
        </InputGroup>
      ) : null}

      {!isSummary ? (
        <InputGroup style={{ marginTop: 16 }}>
          <Label>Texto de ajuda</Label>
          <TextArea
            value={field.helpText}
            onChange={(e) => updateField({ helpText: e.target.value })}
            placeholder="Ajuda opcional para o usuário"
          />
        </InputGroup>
      ) : null}

      {!isSummary ? (
        <ToggleRow>
          <input
            type="checkbox"
            checked={field.required}
            onChange={(e) => updateField({ required: e.target.checked })}
          />
          Obrigatório
        </ToggleRow>
      ) : null}

      {hasOptions ? (
        <InputGroup style={{ marginTop: 16 }}>
          <Label>Opções</Label>
          {(field.options || []).map((option, index) => (
            <OptionRow key={`${field.id}-option-${index}`}>
              <Input
                value={option.label}
                onChange={(e) => updateOption(index, "label", e.target.value)}
                placeholder="Label"
              />
              <Input
                value={option.value}
                onChange={(e) => updateOption(index, "value", e.target.value)}
                placeholder="Valor"
              />
              <ActionButton onClick={() => removeOption(index)}>Remover</ActionButton>
            </OptionRow>
          ))}
          <AddOptionButton onClick={addOption}>Adicionar opção</AddOptionButton>
        </InputGroup>
      ) : null}

      {isSlider ? (
        <Grid style={{ marginTop: 16 }}>
          <InputGroup>
            <Label>Valor mínimo</Label>
            <Input
              value={field.min}
              type="number"
              onChange={(e) => updateField({ min: e.target.value })}
            />
          </InputGroup>
          <InputGroup>
            <Label>Valor máximo</Label>
            <Input
              value={field.max}
              type="number"
              onChange={(e) => updateField({ max: e.target.value })}
            />
          </InputGroup>
          <InputGroup>
            <Label>Passo</Label>
            <Input
              value={field.step}
              type="number"
              onChange={(e) => updateField({ step: e.target.value })}
            />
          </InputGroup>
          <InputGroup>
            <Label>Tipo do valor</Label>
            <Input
              value={field.valueType}
              onChange={(e) => updateField({ valueType: e.target.value })}
              placeholder="integer ou float"
            />
          </InputGroup>
        </Grid>
      ) : null}

      {isImageClick ? (
        <InputGroup style={{ marginTop: 16 }}>
          <Label>URL da imagem</Label>
          <Input
            value={field.imageUrl}
            onChange={(e) => updateField({ imageUrl: e.target.value })}
            placeholder="https://..."
          />
        </InputGroup>
      ) : null}

      {!isSummary ? (
        <Section>
          <SectionToggle type="button" onClick={() => setShowValidation((current) => !current)}>
            <span>Validação</span>
            <SectionHint>{showValidation ? "Ocultar" : "Exibir"}</SectionHint>
          </SectionToggle>

          {showValidation ? (
            <SectionContent>
              {isTextualField ? (
                <Grid>
                  <InputGroup>
                    <Label>Máscara</Label>
                    <NativeSelect value={selectedMaskType} onChange={handleMaskChange}>
                      {FIELD_MASK_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </NativeSelect>
                  </InputGroup>

                  {selectedMaskType === "custom" ? (
                    <InputGroup>
                      <Label>Pattern</Label>
                      <Input
                        value={field.pattern}
                        onChange={(e) => updateField({ pattern: e.target.value, maskType: "custom" })}
                        placeholder="Ex.: \\d{2}\\/\\d{2}\\/\\d{4}"
                      />
                    </InputGroup>
                  ) : null}

                  <InputGroup>
                    <Label>Min. caracteres</Label>
                    <Input
                      value={field.minLength}
                      type="number"
                      onChange={(e) => updateField({ minLength: e.target.value })}
                      placeholder="Opcional"
                    />
                  </InputGroup>
                  <InputGroup>
                    <Label>Máx. caracteres</Label>
                    <Input
                      value={field.maxLength}
                      type="number"
                      onChange={(e) => updateField({ maxLength: e.target.value })}
                      placeholder="Opcional"
                    />
                  </InputGroup>
                </Grid>
              ) : null}

              <InputGroup style={{ marginTop: isTextualField ? 16 : 0 }}>
                <Label>Mensagem de erro</Label>
                <Input
                  value={field.errorMessage}
                  onChange={(e) => updateField({ errorMessage: e.target.value })}
                  placeholder="Mensagem opcional para validação"
                />
              </InputGroup>
            </SectionContent>
          ) : null}
        </Section>
      ) : null}

      <ActionRow>
        <ActionButton onClick={onClose}>Fechar</ActionButton>
      </ActionRow>
    </Container>
  );
}

export default FormFieldEditor;
